export interface FlightLeg {
  departureAirport: string
  departureAirportName: string
  departureCity: string
  departureTime: string
  departureDate: string
  arrivalAirport: string
  arrivalAirportName: string
  arrivalCity: string
  arrivalTime: string
  arrivalDate: string
  durationHours: number
  durationMinutes: number
  layovers: Array<{
    airportCode: string
    airportName: string
    city?: string
    durationHours?: number
    durationMinutes?: number
    // Local times at the layover airport: when the inbound flight lands and the outbound flight departs.
    arrivalTime?: string
    arrivalDate?: string
    departureTime?: string
    departureDate?: string
  }>
  hiddenStops?: Array<{
    airportCode: string
    arrivalTime?: string
    departureTime?: string
    elapsedTime?: {
      hours: number
      minutes: number
    }
    elapsedLayoverTime?: {
      hours: number
      minutes: number
    }
  }>
  airline: string
  airlineLogo: string
  flightNumber: string
  operatingAirline?: string
  aircraftType?: string
}

export interface LeFlightOptionSavedItemData {
  source: 'le_flight'
  searchId?: string
  searchExpiry?: number
  searchParams?: string
  journeyKey?: string
  journeyId?: string
  currencyCode?: string
  departureDate?: string
  arrivalDate?: string
}

export interface FlightOption {
  leg: FlightLeg
  price: number
  pricePerPerson: number
  currencyCode?: string
  carrier: string
  carrierLogo: string
  carrierBrandMark?: string
  cabin: string
  journeyKey?: string
  savedItemData?: LeFlightOptionSavedItemData
  includedBaggage?: { carryOn?: string; checked?: string }
  // LuxPlus+ member pricing (inverse model): `price` is the member fare,
  // `regularPrice` is the struck-through public "was" price. Present only on
  // qualifying member fares.
  regularPrice?: number
  regularPricePerPerson?: number
  isLuxPlusFare?: boolean
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const isString = (value: unknown): value is string =>
  typeof value === 'string'

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

// LuxPlus+ pricing fields are optional everywhere; reject only malformed values
// so a bad regularPrice can't break the struck-through display.
const hasValidLuxPlusPricingFields = (value: Record<string, unknown>): boolean =>
  (value.regularPrice === undefined || isNumber(value.regularPrice)) &&
  (value.regularPricePerPerson === undefined || isNumber(value.regularPricePerPerson)) &&
  (value.isLuxPlusFare === undefined || typeof value.isLuxPlusFare === 'boolean')

const isDuration = (value: unknown): value is { hours: number; minutes: number } => {
  if (!isObjectRecord(value)) return false

  return isNumber(value.hours) && isNumber(value.minutes)
}

const isFlightLayover = (value: unknown): value is FlightLeg['layovers'][number] => {
  if (!isObjectRecord(value)) return false

  return (
    isString(value.airportCode) &&
    isString(value.airportName) &&
    (value.city === undefined || isString(value.city)) &&
    (value.durationHours === undefined || isNumber(value.durationHours)) &&
    (value.durationMinutes === undefined || isNumber(value.durationMinutes)) &&
    (value.arrivalTime === undefined || isString(value.arrivalTime)) &&
    (value.arrivalDate === undefined || isString(value.arrivalDate)) &&
    (value.departureTime === undefined || isString(value.departureTime)) &&
    (value.departureDate === undefined || isString(value.departureDate))
  )
}

const isHiddenStop = (value: unknown): value is NonNullable<FlightLeg['hiddenStops']>[number] => {
  if (!isObjectRecord(value)) return false

  return (
    isString(value.airportCode) &&
    (value.arrivalTime === undefined || isString(value.arrivalTime)) &&
    (value.departureTime === undefined || isString(value.departureTime)) &&
    (value.elapsedTime === undefined || isDuration(value.elapsedTime)) &&
    (value.elapsedLayoverTime === undefined || isDuration(value.elapsedLayoverTime))
  )
}

const isSavedItemData = (value: unknown): value is LeFlightOptionSavedItemData => {
  if (!isObjectRecord(value)) return false

  return (
    value.source === 'le_flight' &&
    (value.searchId === undefined || isString(value.searchId)) &&
    (value.searchExpiry === undefined || isNumber(value.searchExpiry)) &&
    (value.searchParams === undefined || isString(value.searchParams)) &&
    (value.journeyKey === undefined || isString(value.journeyKey)) &&
    (value.journeyId === undefined || isString(value.journeyId)) &&
    (value.currencyCode === undefined || isString(value.currencyCode)) &&
    (value.departureDate === undefined || isString(value.departureDate)) &&
    (value.arrivalDate === undefined || isString(value.arrivalDate))
  )
}

const isFlightLeg = (value: unknown): value is FlightLeg => {
  if (!isObjectRecord(value)) return false

  return (
    isString(value.departureAirport) &&
    isString(value.departureAirportName) &&
    isString(value.departureCity) &&
    isString(value.departureTime) &&
    isString(value.departureDate) &&
    isString(value.arrivalAirport) &&
    isString(value.arrivalAirportName) &&
    isString(value.arrivalCity) &&
    isString(value.arrivalTime) &&
    isString(value.arrivalDate) &&
    isNumber(value.durationHours) &&
    isNumber(value.durationMinutes) &&
    Array.isArray(value.layovers) &&
    value.layovers.every(isFlightLayover) &&
    (value.hiddenStops === undefined || (
      Array.isArray(value.hiddenStops) &&
      value.hiddenStops.every(isHiddenStop)
    )) &&
    isString(value.airline) &&
    isString(value.airlineLogo) &&
    isString(value.flightNumber)
  )
}

const isFlightOption = (value: unknown): value is FlightOption => {
  if (!isObjectRecord(value)) return false

  return (
    isFlightLeg(value.leg) &&
    isNumber(value.price) &&
    isNumber(value.pricePerPerson) &&
    (value.currencyCode === undefined || isString(value.currencyCode)) &&
    isString(value.carrier) &&
    isString(value.carrierLogo) &&
    isString(value.cabin) &&
    (value.journeyKey === undefined || isString(value.journeyKey)) &&
    (value.savedItemData === undefined || isSavedItemData(value.savedItemData)) &&
    (value.regularPrice === undefined || isNumber(value.regularPrice)) &&
    (value.regularPricePerPerson === undefined || isNumber(value.regularPricePerPerson)) &&
    (value.isLuxPlusFare === undefined || typeof value.isLuxPlusFare === 'boolean')
  )
}

const isFlightRecommendationItem = (value: unknown): value is FlightRecommendationSingleItem => {
  if (!isObjectRecord(value)) return false
  const isReturnTrip = value.tripType === 'Return'

  return (
    isFlightOption(value.outbound) &&
    (isReturnTrip ? isFlightOption(value.inbound) : value.inbound === undefined) &&
    isNumber(value.totalPrice) &&
    isNumber(value.pricePerPerson) &&
    isString(value.currencyCode) &&
    isString(value.tripType) &&
    // Keep `tripType` a true discriminant: a single-trip item must never claim
    // to be multi-city, so the render-time guard can rely on it alone.
    value.tripType !== 'MultiCity' &&
    isString(value.searchQuery) &&
    hasValidLuxPlusPricingFields(value)
  )
}

const isMultiCityFlightRecommendationData = (
  value: unknown,
): value is FlightRecommendationMultiCityItem => {
  if (!isObjectRecord(value)) return false

  return (
    value.tripType === 'MultiCity' &&
    Array.isArray(value.flights) &&
    value.flights.length > 0 &&
    value.flights.every(isFlightOption) &&
    isNumber(value.totalPrice) &&
    isNumber(value.pricePerPerson) &&
    isString(value.currencyCode) &&
    isString(value.searchQuery) &&
    hasValidLuxPlusPricingFields(value)
  )
}

export function isMultiCityFlightRecommendationItem(
  item: FlightRecommendationItem,
): item is FlightRecommendationMultiCityItem {
  return item.tripType === 'MultiCity'
}

export function getFlightOptionJourneyKey(option: FlightOption): string | undefined {
  return option.savedItemData?.journeyKey ?? option.journeyKey
}

function toHiddenStop(hiddenStop: NonNullable<FlightLeg['hiddenStops']>[number]): App.HiddenStop {
  return {
    airportCode: hiddenStop.airportCode,
    arrivalTime: hiddenStop.arrivalTime ?? '',
    departureTime: hiddenStop.departureTime ?? '',
    elapsedTime: hiddenStop.elapsedTime ?? { hours: 0, minutes: 0 },
    elapsedLayoverTime: hiddenStop.elapsedLayoverTime ?? { hours: 0, minutes: 0 },
  }
}

export interface FlightFare {
  outbound: FlightLeg
  inbound?: FlightLeg
  carrier: string
  carrierLogo: string
  carrierBrandMark?: string
  totalPrice: number
  pricePerPerson: number
  passengers?: number
  currencyCode: string
  cabin: string
  journeyKey?: string
  savedItemData?: LeFlightOptionSavedItemData
  includedBaggage?: { carryOn?: string; checked?: string }
  searchId?: string
  searchExpiry?: number
  searchParams?: string
  regularPrice?: number
  regularPricePerPerson?: number
  isLuxPlusFare?: boolean
}

export interface FlightRecommendationSingleItem {
  outbound: FlightOption
  inbound?: FlightOption
  totalPrice: number
  pricePerPerson: number
  currencyCode: string
  tripType: string
  searchQuery: string
  passengers?: number
  searchId?: string
  sessionId?: string
  searchExpiry?: number
  searchParams?: string
  regularPrice?: number
  regularPricePerPerson?: number
  isLuxPlusFare?: boolean
}

export interface FlightMultiCityOption extends FlightOption {
  optionRef: string
}

export interface FlightMultiCitySegment {
  segmentIndex: number
  route: string
  departureDate: string
  optionCount: number
  options: Array<FlightMultiCityOption>
}

export interface FlightMultiCityTransition {
  selectedOptionRefs: Array<string>
  nextOptions: Array<{
    optionRef: string
    price: number
    pricePerPerson: number
    currencyCode?: string
  }>
}

export interface FlightMultiCityCompatibleItinerary {
  itineraryRef: string
  optionRefs: Array<string>
  totalPrice: number
  pricePerPerson: number
  currencyCode?: string
}

export interface FlightRecommendationMultiCityItem {
  flights: Array<FlightOption>
  totalPrice: number
  pricePerPerson: number
  currencyCode: string
  tripType: 'MultiCity'
  searchQuery: string
  passengers?: number
  searchId?: string
  sessionId?: string
  searchExpiry?: number
  searchParams?: string
  regularPrice?: number
  regularPricePerPerson?: number
  isLuxPlusFare?: boolean
}

export type FlightRecommendationItem =
  | FlightRecommendationSingleItem
  | FlightRecommendationMultiCityItem

export interface FlightRecommendationsUIData {
  type: 'flight_recommendations'
  items: Array<FlightRecommendationItem>
}

export interface FlightUIData {
  type: 'flight'
  tripType: string
  searchQuery: string
  fares?: Array<FlightFare>
  outboundOptions?: Array<FlightOption>
  inboundOptions?: Array<FlightOption>
  // Multi-city results are delivered per-leg in `segments` (with `fares` empty
  // and no outbound/inbound options). Each segment carries its own option list;
  // the user picks one option per segment via the stepper.
  segments?: Array<FlightMultiCitySegment>
  compatibleTransitions?: Array<FlightMultiCityTransition>
  compatibleItineraries?: Array<FlightMultiCityCompatibleItinerary>
  passengers?: number
  currencyCode: string
  searchId?: string
  sessionId?: string
  searchExpiry?: number
  searchParams?: string
}

/**
 * Adapts raw backend contract data into a typed FlightUIData, isolating the
 * unsafe cast at this single boundary rather than scattering `as unknown as`
 * through component code.
 */
export function toFlightUIData(raw: unknown): FlightUIData {
  return normalizeFlightUIData(raw as FlightUIData)
}

export function toFlightRecommendationsUIData(raw: unknown): FlightRecommendationsUIData {
  if (!isObjectRecord(raw)) {
    return { type: 'flight_recommendations', items: [] }
  }

  const items: Array<FlightRecommendationItem> = []
  if (Array.isArray(raw.items)) {
    raw.items.forEach(item => {
      if (isFlightRecommendationItem(item)) {
        items.push(item)
        return
      }
      if (isMultiCityFlightRecommendationData(item)) {
        items.push(item)
      }
    })
  }

  return {
    type: 'flight_recommendations',
    items,
  }
}

/**
 * The backend may send flight data in either `outboundOptions`/`inboundOptions`
 * format or the older `fares` array format. This normalizes both into the
 * canonical `outboundOptions`/`inboundOptions` shape.
 */
export function normalizeFlightUIData(data: FlightUIData): FlightUIData {
  if (data.outboundOptions?.length) return data
  if (!data.fares?.length) return data

  const firstFare = data.fares[0]

  const outboundOptions: Array<FlightOption> = data.fares.map(fare => ({
    leg: fare.outbound,
    price: fare.totalPrice,
    pricePerPerson: fare.pricePerPerson,
    currencyCode: fare.currencyCode,
    carrier: fare.carrier,
    carrierLogo: fare.carrierLogo,
    carrierBrandMark: fare.carrierBrandMark,
    cabin: fare.cabin,
    journeyKey: fare.journeyKey,
    savedItemData: fare.savedItemData,
    includedBaggage: fare.includedBaggage,
    regularPrice: fare.regularPrice,
    regularPricePerPerson: fare.regularPricePerPerson,
    isLuxPlusFare: fare.isLuxPlusFare,
  }))

  const inboundFares = data.fares.filter((fare): fare is FlightFare & { inbound: FlightLeg } => !!fare.inbound)
  const inboundOptions: Array<FlightOption> | undefined = inboundFares.length > 0
    ? inboundFares.map(fare => ({
        leg: fare.inbound,
        price: fare.totalPrice,
        pricePerPerson: fare.pricePerPerson,
        currencyCode: fare.currencyCode,
        carrier: fare.carrier,
        carrierLogo: fare.carrierLogo,
        carrierBrandMark: fare.carrierBrandMark,
        cabin: fare.cabin,
        journeyKey: fare.journeyKey,
        savedItemData: fare.savedItemData,
        includedBaggage: fare.includedBaggage,
        regularPrice: fare.regularPrice,
        regularPricePerPerson: fare.regularPricePerPerson,
        isLuxPlusFare: fare.isLuxPlusFare,
      }))
    : undefined

  return {
    ...data,
    outboundOptions,
    inboundOptions,
    currencyCode: data.currencyCode || firstFare.currencyCode,
    passengers: data.passengers ?? firstFare.passengers,
    searchId: data.searchId ?? firstFare.searchId,
    sessionId: data.sessionId,
    searchExpiry: data.searchExpiry ?? firstFare.searchExpiry,
    searchParams: data.searchParams ?? firstFare.searchParams,
  }
}

export function optionToJourney(option: FlightOption, id: string, currencyCode: string): App.JourneyV2 {
  const { leg } = option
  const hiddenStops = (leg.hiddenStops ?? []).map(toHiddenStop)
  const journeyId = option.savedItemData?.journeyId ?? id
  const journeyKey = getFlightOptionJourneyKey(option) ?? ''
  const totalFare = option.pricePerPerson

  const airlineInfo: App.FlightV2Airline = {
    code: leg.operatingAirline || leg.airline || option.carrier,
    name: option.carrier,
    logoUrl: leg.airlineLogo || option.carrierLogo,
  }

  const buildFlight = (
    flightId: string,
    departureAirport: string,
    arrivalAirport: string,
    departureAirportName: string,
    arrivalAirportName: string,
    departingDate: string,
    arrivalDate: string,
    departingTime: string,
    arrivalTime: string,
    duration: { hours: number; minutes: number },
    stopoverTime: { hours: number; minutes: number },
    flightHiddenStops: Array<App.HiddenStop>,
    flightNumber: string,
  ): App.FlightV2 => ({
    id: flightId,
    arrivalAirport,
    arrivalAirportName,
    arrivalDateTime: arrivalDate && arrivalTime ? `${arrivalDate}T${arrivalTime}:00` : '',
    departureAirport,
    departureAirportName,
    departureDateTime: departingDate && departingTime ? `${departingDate}T${departingTime}:00` : '',
    departureTerminal: '',
    arrivalTerminal: '',
    duration,
    departureCountry: '',
    arrivalCountry: '',
    flyTime: duration.hours * 60 + duration.minutes,
    aircraftType: leg.aircraftType || '',
    capacity: '',
    flightNumber,
    marketingAirline: airlineInfo,
    operatingAirline: airlineInfo,
    departureCity: '',
    arrivalCity: '',
    fareClass: option.cabin,
    flightBookingKey: journeyKey,
    seatsSelectionAvailable: '',
    carryOnBaggageOptions: [],
    checkedBaggageOptions: [],
    status: '',
    itemType: 'flightV2',
    hasStopover: leg.layovers.length > 0 || flightHiddenStops.length > 0,
    stopoverTime,
    departingTime,
    departingDate,
    arrivalTime,
    arrivalDate,
    bookingClass: option.cabin,
    seatSummary: {
      selectionAvailable: false,
      selectionPolicy: '',
    },
    hiddenStops: flightHiddenStops,
  })

  // The first segment arrives at the first layover airport (or the destination when non-stop).
  // Layover arrival/departure times are the local times at the stop; when the payload omits them
  // we fall back to leg-level placeholders (which are only accurate for the origin/final endpoints).
  const firstLayover = leg.layovers[0]
  const firstLayoverArrival = firstLayover?.arrivalDate !== undefined && firstLayover.arrivalTime !== undefined
    ? { date: firstLayover.arrivalDate, time: firstLayover.arrivalTime }
    : { date: leg.arrivalDate, time: leg.arrivalTime }
  const flights: Array<App.FlightV2> = [
    buildFlight(
      `${journeyId}-flight-0`,
      leg.departureAirport,
      firstLayover ? firstLayover.airportCode : leg.arrivalAirport,
      leg.departureAirportName,
      firstLayover ? firstLayover.airportName : leg.arrivalAirportName,
      leg.departureDate,
      firstLayoverArrival.date,
      leg.departureTime,
      firstLayoverArrival.time,
      { hours: leg.durationHours, minutes: leg.durationMinutes },
      firstLayover
        ? { hours: firstLayover.durationHours ?? 0, minutes: firstLayover.durationMinutes ?? 0 }
        : { hours: 0, minutes: 0 },
      hiddenStops,
      leg.flightNumber,
    ),
  ]

  for (let i = 0; i < leg.layovers.length; i++) {
    const layover = leg.layovers[i]
    const nextLayover = leg.layovers[i + 1]
    const isLastLayover = i === leg.layovers.length - 1
    const nextAirport = nextLayover ? nextLayover.airportCode : leg.arrivalAirport
    const layoverDeparture = layover.departureDate !== undefined && layover.departureTime !== undefined
      ? { date: layover.departureDate, time: layover.departureTime }
      : { date: leg.departureDate, time: '' }
    const nextLayoverArrival = nextLayover?.arrivalDate !== undefined && nextLayover.arrivalTime !== undefined
      ? { date: nextLayover.arrivalDate, time: nextLayover.arrivalTime }
      : { date: leg.departureDate, time: '' }

    flights.push(buildFlight(
      `${journeyId}-flight-${i + 1}`,
      layover.airportCode,
      nextAirport,
      layover.airportName,
      isLastLayover ? leg.arrivalAirportName : (nextLayover?.airportName ?? ''),
      layoverDeparture.date,
      isLastLayover ? leg.arrivalDate : nextLayoverArrival.date,
      layoverDeparture.time,
      isLastLayover ? leg.arrivalTime : nextLayoverArrival.time,
      { hours: 0, minutes: 0 },
      nextLayover
        ? { hours: nextLayover.durationHours ?? 0, minutes: nextLayover.durationMinutes ?? 0 }
        : { hours: 0, minutes: 0 },
      [],
      '',
    ))
  }

  const price: App.JourneyV2['price'] = {
    all: {
      baseFare: totalFare,
      baseFareOriginalCurrency: totalFare,
      totalFareOriginalCurrency: totalFare,
      totalFare,
      totalMarkup: 0,
      totalGst: 0,
      totalTax: 0,
      totalFees: 0,
      totalTaxOriginalCurrency: 0,
      currencyRate: 1,
      currencyMarkup: '',
      currencyCode,
      bookingFee: 0,
      serviceFee: 0,
      atolFee: 0,
      flightsCreditFee: 0,
      totalRoundTripPrice: totalFare,
    },
    adult: {
      passengerTypeQuantity: { quantity: 1, code: 'ADT' },
      totalFare,
      currencyCode,
    },
    child: {
      passengerTypeQuantity: { quantity: 0, code: 'CHD' },
      totalFare: 0,
      currencyCode,
    },
    infant: {
      passengerTypeQuantity: { quantity: 0, code: 'INF' },
      totalFare: 0,
      currencyCode,
    },
  }

  // Inverse model: `price` above is the member fare; `regularPrice` mirrors it
  // with the public per-person "was" fare so the card can strike it through.
  const regularTotalFare = option.regularPricePerPerson
  const regularPrice: App.JourneyV2['regularPrice'] | undefined = regularTotalFare !== undefined ? {
    all: {
      baseFare: regularTotalFare,
      baseFareOriginalCurrency: regularTotalFare,
      totalFareOriginalCurrency: regularTotalFare,
      totalFare: regularTotalFare,
      totalMarkup: 0,
      totalGst: 0,
      totalTax: 0,
      totalFees: 0,
      totalTaxOriginalCurrency: 0,
      currencyRate: 1,
      currencyMarkup: '',
      currencyCode,
      bookingFee: 0,
      serviceFee: 0,
      atolFee: 0,
      flightsCreditFee: 0,
      totalRoundTripPrice: regularTotalFare,
    },
    adult: {
      passengerTypeQuantity: { quantity: 1, code: 'ADT' },
      totalFare: regularTotalFare,
      currencyCode,
    },
    child: {
      passengerTypeQuantity: { quantity: 0, code: 'CHD' },
      totalFare: 0,
      currencyCode,
    },
    infant: {
      passengerTypeQuantity: { quantity: 0, code: 'INF' },
      totalFare: 0,
      currencyCode,
    },
  } : undefined

  return {
    id: journeyId,
    provider: 'sabre',
    name: option.carrier,
    fareBasisCode: '',
    carrier: option.carrier,
    carrierName: option.carrier,
    carrierLogo: option.carrierLogo,
    carrierBrandMark: option.carrierBrandMark,
    displayName: option.carrier,
    cabin: option.cabin,
    currencyCode,
    currencySymbol: '',
    isInboundCarrierSameFilter: false,
    itemType: 'journeyV2',
    pairedFareId: [],
    price,
    ...(regularPrice !== undefined && { regularPrice }),
    flightGroup: {
      id: `${id}-group`,
      flightGroupNumber: 0,
      flights,
      totalStopOverTime: { hours: 0, minutes: 0 },
      totalTravelDuration: { hours: leg.durationHours, minutes: leg.durationMinutes },
      totalFlightDuration: { hours: leg.durationHours, minutes: leg.durationMinutes },
      numberOfStops: leg.layovers.length + hiddenStops.length,
    },
    bookingInfo: {
      journeyKey,
      providerSearchId: option.savedItemData?.searchId ?? '',
      boookingKey: journeyKey,
      bookingClass: option.cabin,
      ...(option.isLuxPlusFare !== undefined && { isLuxPlusFare: option.isLuxPlusFare }),
    },
    fareType: 'OneWay',
    links: [],
  }
}
